import type { Route } from "nextjs-routes";

// SEO 优化的默认描述 - 155 字符以内
const DEFAULT_TEMPLATE =
  "Morph is the consumer blockchain for everyday crypto payments. Explore transactions, tokens, smart contracts and network analytics on Morph Explorer.";

// SEO 优化的页面描述模板 - 每个页面独立描述，包含关键词
const TEMPLATE_MAP: Record<Route["pathname"], string> = {
  "/": "Morph Explorer - Your gateway to blockchain analytics. Track transactions, explore smart contracts, view token balances, and monitor network activity in real-time.",
  "/txs":
    "Browse all %network_name% transactions in real-time. View transaction details, gas fees, status, and block confirmations on the Morph blockchain explorer.",
  "/internal-txs":
    "Explore internal transactions and smart contract calls on %network_name%. Track internal ETH transfers and contract interactions.",
  "/txs/kettle/[hash]":
    "View all transactions for kettle %hash% on %network_name%. Explore transaction history and activity.",
  "/tx/[hash]":
    "Transaction %hash% details on %network_name%. View gas used, status, input data, logs, and state changes.",
  "/blocks":
    "Explore the latest blocks on %network_name%. View block details, transactions count, gas usage, and block rewards in real-time.",
  "/block/[height_or_hash]":
    "Block %height_or_hash% on %network_name%. View transactions, gas used, timestamp, and block producer details.",
  "/block/countdown":
    "Track upcoming blocks on %network_name%. Set countdown timers for target block heights and estimated arrival times.",
  "/block/countdown/[height]":
    "Countdown to block %height% on %network_name%. View estimated time to target block and current block progress.",
  "/accounts":
    "Top accounts on %network_name% ranked by balance. Explore whale wallets, smart contracts, and the richest addresses on the network.",
  "/accounts/label/[slug]":
    "Find %network_name% addresses labeled as %slug%. Search verified contracts, exchanges, and known wallets.",
  "/address/[hash]":
    "Address %hash% on %network_name%. View balance, transaction history, token holdings, and contract interactions.",
  "/verified-contracts":
    "Browse verified smart contracts on %network_name%. Find open-source contracts with verified source code and ABIs.",
  "/contract-verification":
    "Verify your smart contract on %network_name%. Upload source code to enable contract interaction and increase transparency.",
  "/address/[hash]/contract-verification":
    "Verify contract %hash% on %network_name%. Upload Solidity source code to verify and publish your smart contract.",
  "/tokens":
    "Explore all tokens on %network_name%. Browse ERC-20 tokens, NFT collections, and token analytics with price and holder data.",
  "/token/[hash]":
    "Token %hash% analytics on %network_name%. View price, holders, transfers, and detailed token information.",
  "/token/[hash]/instance/[id]":
    "NFT #%id% details on %network_name%. View ownership history, metadata, attributes, and current holder.",
  "/apps":
    "Discover decentralized apps on %network_name%. Explore DeFi, NFT marketplaces, games, and more built on Morph.",
  "/apps/[id]":
    "DApp details on %network_name%. View app information, smart contracts, and on-chain activity.",
  "/stats":
    "Network statistics and analytics for %network_name%. Charts for transactions, addresses, gas usage, and blockchain metrics.",
  "/stats/[id]":
    "%id% statistics on %network_name%. Historical data and trend analysis for network metrics.",
  "/api-docs":
    "%network_name% API documentation for developers. REST and GraphQL endpoints for blockchain data integration.",
  "/search-results":
    'Search results for "%q%" on %network_name%. Find addresses, transactions, tokens, and blocks.',
  "/auth/profile":
    "Manage your %network_name% Explorer profile. View watchlists, API keys, and account settings.",
  "/account/merits":
    "Your Morph Merits rewards. Track earned points, achievements, and reward history.",
  "/account/watchlist":
    "Your %network_name% address watchlist. Monitor balances and transactions for tracked addresses.",
  "/account/api-key":
    "Manage your %network_name% API keys. Create and configure API access for development.",
  "/account/custom-abi":
    "Custom contract ABIs on %network_name%. Add ABIs to interact with unverified contracts.",
  "/account/tag-address":
    "Private address tags on %network_name%. Label addresses for easy identification.",
  "/account/verified-addresses":
    "Your verified addresses on %network_name%. Prove ownership and manage verified accounts.",
  "/public-tags/submit":
    "Submit a public tag for %network_name%. Help identify contracts, exchanges, and notable addresses for the community.",
  "/withdrawals":
    "Track L2 to L1 withdrawals on %network_name%. Monitor withdrawal status, proofs, and finalization times.",
  "/txn-withdrawals":
    "L2 to L1 message relayer on %network_name%. Track cross-layer messages and withdrawal proofs.",
  "/visualize/sol2uml":
    "Visualize smart contracts on %network_name% as UML diagrams. Understand contract structure and inheritance.",
  "/csv-export":
    "Export %network_name% blockchain data to CSV. Download transactions, token transfers, and address data.",
  "/deposits":
    "Track L1 to L2 deposits on %network_name%. Monitor bridge deposits and confirmation status.",
  "/output-roots":
    "L2 output roots on %network_name%. View state commitments and proof submissions to L1.",
  "/dispute-games":
    "Dispute games on %network_name%. Track fault proofs and challenge submissions.",
  "/batches":
    "Transaction batches on %network_name%. View L2 batch submissions and data availability.",
  "/batches/[number]":
    "Batch %number% details on %network_name%. View transactions, timestamp, and L1 submission.",
  "/batches/celestia/[height]/[commitment]":
    "Celestia DA batch on %network_name%. View data availability layer details.",
  "/blobs/[hash]":
    "Blob %hash% on %network_name%. View EIP-4844 blob data and transaction details.",
  "/ops":
    "User operations on %network_name%. Explore Account Abstraction (ERC-4337) transactions and bundler activity.",
  "/op/[hash]":
    "User operation %hash% on %network_name%. View AA transaction details, paymaster, and execution.",
  "/latest-l1-l2":
    "Latest L1 to L2 deposits on %network_name%. Real-time bridge deposit tracking from Ethereum mainnet.",
  "/latest-l2-l1":
    "Latest L2 to L1 withdrawals on %network_name%. Real-time bridge withdrawal tracking to Ethereum mainnet.",
  "/404":
    "Page not found on %network_name% Explorer. The requested page does not exist.",
  "/name-domains":
    "Name domains on %network_name%. Explore ENS and name service registrations.",
  "/name-domains/[name]":
    "%name% domain on %network_name%. View owner, resolver, and domain records.",
  "/validators":
    "Network validators on %network_name%. View validator set, stake amounts, and performance.",
  "/validators/[id]":
    "Validator %id% on %network_name%. View stake, blocks produced, and validator details.",
  "/epochs":
    "Consensus epochs on %network_name%. View epoch history and validator participation.",
  "/epochs/[number]":
    "Epoch %number% on %network_name%. View blocks, validators, and epoch statistics.",
  "/gas-tracker":
    "Real-time gas tracker for %network_name%. Get accurate gas price estimates in %network_gwei% for transactions.",
  "/mud-worlds":
    "MUD worlds on %network_name%. Explore on-chain game worlds and autonomous world applications.",
  "/token-transfers":
    "Token transfers on %network_name%. Track ERC-20 and NFT transfers across the network.",
  "/advanced-filter":
    "Advanced transaction filter for %network_name%. Search with complex queries and filters.",
  "/pools":
    "DEX liquidity pools on %network_name%. View pool analytics, TVL, and trading activity.",
  "/pools/[hash]":
    "Pool %hash% on %network_name%. View liquidity, volume, fees, and pool details.",
  "/interop-messages":
    "Cross-chain interop messages on %network_name%. Track messages between chains.",
  "/operations":
    "Network operations on %network_name%. View system activities and protocol operations.",
  "/operation/[id]":
    "Operation %id% details on %network_name%. View operation status and transaction data.",
  "/references":
    "Browse reference keys on %network_name%. View reference IDs, transaction counts, gas fees, and trace transactions by reference key on Morph Explorer.",
  "/reference/[reference]":
    "Reference %reference% on %network_name%. View all transactions linked to this reference key, with hashes, from/to addresses, fees and status.",

  // multichain routes
  "/chain/[chain-slug]/accounts/label/[slug]":
    "Find %network_name% addresses by label %slug%. Search verified accounts and known entities.",
  "/chain/[chain-slug]/advanced-filter":
    "Advanced search on %network_name%. Filter transactions with complex queries.",
  "/chain/[chain-slug]/block/[height_or_hash]":
    "Block %height_or_hash% on %network_name%. View block details and transactions.",
  "/chain/[chain-slug]/block/countdown":
    "Block countdown on %network_name%. Track target block arrival times.",
  "/chain/[chain-slug]/block/countdown/[height]":
    "Countdown to block %height% on %network_name%.",
  "/chain/[chain-slug]/csv-export": "Export %network_name% data to CSV format.",
  "/chain/[chain-slug]/op/[hash]":
    "User operation %hash% on %network_name%. View AA transaction details.",
  "/chain/[chain-slug]/token/[hash]":
    "Token analytics on %network_name%. View holders and transfers.",
  "/chain/[chain-slug]/token/[hash]/instance/[id]":
    "NFT #%id% details on %network_name%.",
  "/chain/[chain-slug]/tx/[hash]":
    "Transaction %hash% on %network_name%. View TX details and logs.",

  // service routes
  "/login":
    "Login to %network_name% Explorer. Access your account and personalized features.",
  "/sprite": DEFAULT_TEMPLATE,
  "/chakra": DEFAULT_TEMPLATE,
  "/api/metrics": DEFAULT_TEMPLATE,
  "/api/monitoring/invalid-api-schema": DEFAULT_TEMPLATE,
  "/api/log": DEFAULT_TEMPLATE,
  "/api/media-type": DEFAULT_TEMPLATE,
  "/api/csrf": DEFAULT_TEMPLATE,
  "/api/healthz": DEFAULT_TEMPLATE,
  "/api/config": DEFAULT_TEMPLATE,
  "/api/sprite": DEFAULT_TEMPLATE,
};

const TEMPLATE_MAP_ENHANCED: Partial<Record<Route["pathname"], string>> = {
  "/stats/[id]": "%description%",
};

export function make(pathname: Route["pathname"], isEnriched = false) {
  return (
    (isEnriched ? TEMPLATE_MAP_ENHANCED[pathname] : undefined) ??
    TEMPLATE_MAP[pathname] ??
    ""
  );
}
